import React, { useEffect, useState, useMemo } from "react";
import {
    Loader2,
    RefreshCw,
    Euro,
    Package,
    Target,
    ShoppingBag,
    TrendingUp,
    Users,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    CartesianGrid,
    Cell,
} from "recharts";
import { toast } from "sonner";
import { formatPrice } from "../lib/orderStatus";

const RANGES = [
    { k: "7d", label: "7 dias" },
    { k: "30d", label: "30 dias" },
    { k: "90d", label: "90 dias" },
];

const LINE_COLORS = {
    fruit: "#B95C50",
    detox: "#7A8B76",
    protein: "#CFA059",
    tea: "#8691A0",
};
const BAR_COLORS = ["#7A8B76", "#B95C50", "#CFA059", "#8691A0", "#1C221F"];

export default function AdminAnalytics({ apiAuth }) {
    const [range, setRange] = useState("30d");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await apiAuth(`/admin/analytics?range=${range}`);
            setData(data);
        } catch {
            toast.error("Erro ao carregar analytics");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const chartData = useMemo(
        () =>
            (data?.sales_series || []).map((d) => ({
                ...d,
                label: new Date(d.date).toLocaleDateString("pt-PT", {
                    day: "2-digit",
                    month: "short",
                }),
            })),
        [data],
    );

    const linesData = useMemo(() => {
        const src = data?.leads_by_line || {};
        return ["fruit", "detox", "protein", "tea"].map((l) => ({
            name: l.toUpperCase(),
            key: l,
            count: src[l] || 0,
        }));
    }, [data]);

    if (busy && !data) {
        return (
            <div className="py-24 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
            </div>
        );
    }

    const s = data?.summary || {};

    return (
        <div className="space-y-6" data-testid="admin-analytics-panel">
            {/* Range + refresh */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="inline-flex bg-white rounded-full p-1 shadow-soft ring-1 ring-brand-green/10">
                    {RANGES.map((r) => (
                        <button
                            key={r.k}
                            type="button"
                            onClick={() => setRange(r.k)}
                            data-testid={`analytics-range-${r.k}`}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
                                range === r.k
                                    ? "bg-brand-green text-white shadow-pop"
                                    : "text-brand-ink-soft hover:text-brand-ink"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors"
                    data-testid="analytics-refresh"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                    Atualizar
                </button>
            </div>

            {/* Summary cards */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard
                    icon={Euro}
                    label="Receita"
                    value={formatPrice(s.revenue || 0)}
                    bg="bg-brand-green"
                    white
                    testid="analytics-revenue"
                />
                <SummaryCard
                    icon={Package}
                    label="Encomendas"
                    value={s.orders ?? 0}
                    subtitle={`${s.units_sold ?? 0} unidades`}
                    bg="bg-white"
                    accent="text-brand-green"
                    testid="analytics-orders"
                />
                <SummaryCard
                    icon={ShoppingBag}
                    label="Ticket médio"
                    value={formatPrice(s.avg_ticket || 0)}
                    bg="bg-brand-orange-soft"
                    accent="text-brand-orange"
                    testid="analytics-avg-ticket"
                />
                <SummaryCard
                    icon={Target}
                    label="Conversão leads"
                    value={`${s.conversion_pct ?? 0}%`}
                    subtitle={`${s.converted_customers ?? 0}/${s.leads ?? 0}`}
                    bg="bg-brand-ink"
                    white
                    testid="analytics-conversion"
                />
            </section>

            {/* Sales chart */}
            <section className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-green">
                    <TrendingUp className="h-3.5 w-3.5" /> Vendas diárias
                </div>
                <div className="h-72 mt-4" data-testid="analytics-sales-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8e2d6" />
                            <XAxis
                                dataKey="label"
                                interval={"preserveStartEnd"}
                                tick={{ fontSize: 11, fill: "#5a5a55" }}
                            />
                            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#5a5a55" }} />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 11, fill: "#5a5a55" }}
                            />
                            <Tooltip
                                contentStyle={{
                                    borderRadius: 12,
                                    border: "1px solid #d5d1c5",
                                    fontSize: 12,
                                }}
                                formatter={(value, key) =>
                                    key === "revenue"
                                        ? [formatPrice(value), "Receita"]
                                        : [value, "Encomendas"]
                                }
                            />
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="revenue"
                                stroke="#7A8B76"
                                strokeWidth={2.5}
                                dot={false}
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="orders"
                                stroke="#B95C50"
                                strokeWidth={2}
                                strokeDasharray="4 3"
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-brand-ink-soft">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-4 rounded-full bg-brand-green" /> Receita
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-4 rounded-full bg-brand-orange border border-brand-orange border-dashed" />{" "}
                        Encomendas
                    </span>
                </div>
            </section>

            {/* Top products & Top kits */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TopList
                    title="Top sabores"
                    rows={data?.top_products || []}
                    accent="text-brand-green"
                    testid="analytics-top-products"
                />
                <TopList
                    title="Top kits"
                    rows={data?.top_kits || []}
                    accent="text-brand-orange"
                    testid="analytics-top-kits"
                />
            </section>

            {/* Leads by line + interest */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-green">
                        <Users className="h-3.5 w-3.5" /> Leads por linha
                    </div>
                    {linesData.every((d) => d.count === 0) ? (
                        <div className="mt-8 text-center text-sm text-brand-ink-soft">
                            Ainda sem leads no período.
                        </div>
                    ) : (
                        <div className="h-56 mt-4" data-testid="analytics-leads-by-line">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={linesData}
                                    margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e2d6" />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: "#5a5a55" }}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 11, fill: "#5a5a55" }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: 12,
                                            border: "1px solid #d5d1c5",
                                            fontSize: 12,
                                        }}
                                    />
                                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                                        {linesData.map((d) => (
                                            <Cell key={d.key} fill={LINE_COLORS[d.key]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-orange">
                        <Users className="h-3.5 w-3.5" /> Interesse dos leads
                    </div>
                    <div className="mt-4 space-y-3">
                        {Object.entries(data?.leads_by_interest || {}).length === 0 ? (
                            <div className="text-sm text-brand-ink-soft text-center py-6">
                                Ainda sem leads no período.
                            </div>
                        ) : (
                            Object.entries(data.leads_by_interest).map(([k, v]) => {
                                const total = Object.values(data.leads_by_interest).reduce(
                                    (a, b) => a + b,
                                    0,
                                );
                                const pct = total ? Math.round((v / total) * 100) : 0;
                                return (
                                    <div key={k}>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="font-semibold text-brand-ink capitalize">
                                                {k}
                                            </span>
                                            <span className="text-brand-ink-soft">
                                                {v} · {pct}%
                                            </span>
                                        </div>
                                        <div className="mt-1 h-2 rounded-full bg-brand-cream overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-brand-green"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

const SummaryCard = ({ icon: Icon, label, value, subtitle, bg, accent, white, testid }) => (
    <div
        data-testid={testid}
        className={`rounded-2xl ${bg} p-5 shadow-soft ring-1 ring-brand-green/5`}
    >
        <Icon className={`h-5 w-5 ${accent || (white ? "text-white/90" : "text-brand-ink-soft")}`} />
        <div
            className={`mt-3 text-[11px] uppercase tracking-widest font-bold ${
                white ? "text-white/70" : "text-brand-ink-soft"
            }`}
        >
            {label}
        </div>
        <div
            className={`mt-1 font-display font-medium text-2xl md:text-3xl ${
                white ? "text-white" : "text-brand-ink"
            }`}
        >
            {value}
        </div>
        {subtitle && (
            <div className={`mt-1 text-xs ${white ? "text-white/70" : "text-brand-ink-soft"}`}>
                {subtitle}
            </div>
        )}
    </div>
);

const TopList = ({ title, rows, accent, testid }) => {
    const max = Math.max(1, ...rows.map((r) => r.units || 0));
    return (
        <div
            className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10"
            data-testid={testid}
        >
            <div className={`flex items-center gap-2 text-xs uppercase tracking-widest font-bold ${accent}`}>
                <TrendingUp className="h-3.5 w-3.5" /> {title}
            </div>
            {rows.length === 0 ? (
                <div className="mt-8 text-center text-sm text-brand-ink-soft">Sem dados no período.</div>
            ) : (
                <ul className="mt-4 space-y-3">
                    {rows.map((r, i) => (
                        <li key={r.product_id}>
                            <div className="flex items-center justify-between text-sm">
                                <span className="font-semibold text-brand-ink truncate">
                                    {i + 1}. {r.name}
                                </span>
                                <span className="text-brand-ink-soft text-xs">
                                    {r.units} un · {formatPrice(r.revenue)}
                                </span>
                            </div>
                            <div className="mt-1 h-2 rounded-full bg-brand-cream overflow-hidden">
                                <div
                                    className="h-full rounded-full"
                                    style={{
                                        width: `${Math.round((r.units / max) * 100)}%`,
                                        backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
                                    }}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
